/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const INITIALIZE_CONFIG_DISCRIMINATOR = new Uint8Array([
  208, 127, 21, 1, 194, 190, 196, 70,
]);

export function getInitializeConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_CONFIG_DISCRIMINATOR,
  );
}

export type InitializeConfigInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountProgramData extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAdmin extends string
        ? WritableSignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountProgramData extends string
        ? ReadonlyAccount<TAccountProgramData>
        : TAccountProgramData,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeConfigInstructionData = {
  discriminator: ReadonlyUint8Array;
  migratorAllowlist: Array<Address>;
  hookAllowlist: Array<Address>;
  protocolFeeBps: number;
  minSwapFeeBps: number;
  maxSwapFeeBps: number;
};

export type InitializeConfigInstructionDataArgs = {
  migratorAllowlist: Array<Address>;
  hookAllowlist: Array<Address>;
  protocolFeeBps: number;
  minSwapFeeBps: number;
  maxSwapFeeBps: number;
};

export function getInitializeConfigInstructionDataEncoder(): Encoder<InitializeConfigInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['migratorAllowlist', getArrayEncoder(getAddressEncoder())],
      ['hookAllowlist', getArrayEncoder(getAddressEncoder())],
      ['protocolFeeBps', getU16Encoder()],
      ['minSwapFeeBps', getU16Encoder()],
      ['maxSwapFeeBps', getU16Encoder()],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_CONFIG_DISCRIMINATOR }),
  );
}

export function getInitializeConfigInstructionDataDecoder(): Decoder<InitializeConfigInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['migratorAllowlist', getArrayDecoder(getAddressDecoder())],
    ['hookAllowlist', getArrayDecoder(getAddressDecoder())],
    ['protocolFeeBps', getU16Decoder()],
    ['minSwapFeeBps', getU16Decoder()],
    ['maxSwapFeeBps', getU16Decoder()],
  ]);
}

export function getInitializeConfigInstructionDataCodec(): Codec<
  InitializeConfigInstructionDataArgs,
  InitializeConfigInstructionData
> {
  return combineCodec(
    getInitializeConfigInstructionDataEncoder(),
    getInitializeConfigInstructionDataDecoder(),
  );
}

export type InitializeConfigAsyncInput<
  TAccountAdmin extends string = string,
  TAccountConfig extends string = string,
  TAccountProgramData extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  admin: TransactionSigner<TAccountAdmin>;
  config?: Address<TAccountConfig>;
  programData: Address<TAccountProgramData>;
  systemProgram?: Address<TAccountSystemProgram>;
  migratorAllowlist: InitializeConfigInstructionDataArgs['migratorAllowlist'];
  hookAllowlist: InitializeConfigInstructionDataArgs['hookAllowlist'];
  protocolFeeBps: InitializeConfigInstructionDataArgs['protocolFeeBps'];
  minSwapFeeBps: InitializeConfigInstructionDataArgs['minSwapFeeBps'];
  maxSwapFeeBps: InitializeConfigInstructionDataArgs['maxSwapFeeBps'];
};

export async function getInitializeConfigInstructionAsync<
  TAccountAdmin extends string,
  TAccountConfig extends string,
  TAccountProgramData extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: InitializeConfigAsyncInput<
    TAccountAdmin,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeConfigInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    admin: { value: input.admin ?? null, isWritable: true },
    config: { value: input.config ?? null, isWritable: true },
    programData: { value: input.programData ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([99, 111, 110, 102, 105, 103])),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('config', accounts.config),
      getAccountMeta('programData', accounts.programData),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeConfigInstructionDataEncoder().encode(
      args as InitializeConfigInstructionDataArgs,
    ),
    programAddress,
  } as InitializeConfigInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >);
}

export type InitializeConfigInput<
  TAccountAdmin extends string = string,
  TAccountConfig extends string = string,
  TAccountProgramData extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  admin: TransactionSigner<TAccountAdmin>;
  config: Address<TAccountConfig>;
  programData: Address<TAccountProgramData>;
  systemProgram?: Address<TAccountSystemProgram>;
  migratorAllowlist: InitializeConfigInstructionDataArgs['migratorAllowlist'];
  hookAllowlist: InitializeConfigInstructionDataArgs['hookAllowlist'];
  protocolFeeBps: InitializeConfigInstructionDataArgs['protocolFeeBps'];
  minSwapFeeBps: InitializeConfigInstructionDataArgs['minSwapFeeBps'];
  maxSwapFeeBps: InitializeConfigInstructionDataArgs['maxSwapFeeBps'];
};

export function getInitializeConfigInstruction<
  TAccountAdmin extends string,
  TAccountConfig extends string,
  TAccountProgramData extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: InitializeConfigInput<
    TAccountAdmin,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeConfigInstruction<
  TProgramAddress,
  TAccountAdmin,
  TAccountConfig,
  TAccountProgramData,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    admin: { value: input.admin ?? null, isWritable: true },
    config: { value: input.config ?? null, isWritable: true },
    programData: { value: input.programData ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('config', accounts.config),
      getAccountMeta('programData', accounts.programData),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeConfigInstructionDataEncoder().encode(
      args as InitializeConfigInstructionDataArgs,
    ),
    programAddress,
  } as InitializeConfigInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig,
    TAccountProgramData,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeConfigInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    admin: TAccountMetas[0];
    config: TAccountMetas[1];
    programData: TAccountMetas[2];
    systemProgram: TAccountMetas[3];
  };
  data: InitializeConfigInstructionData;
};

export function parseInitializeConfigInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeConfigInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 4,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      admin: getNextAccount(),
      config: getNextAccount(),
      programData: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeConfigInstructionDataDecoder().decode(instruction.data),
  };
}
